import { useEffect, useRef, useState } from "react";
import { faFilter } from "@fortawesome/pro-regular-svg-icons";

import { useDebounce } from "common/hooks";
import { WithStringValues } from "common/utils/types";
import { toCount } from "common/utils/formatting";
import Icon from "common/components/Icon";
import Spinner from "common/components/Spinner";

import SearchIcon from "client/common/components/icons/SearchIcon";
import PillButton from "client/common/components/buttons/PillButton";
import Results from "client/common/components/results/Results";
import ModalPortal from "client/common/components/deprecated-modals/ModalPortal";
import TextButton from "client/common/components/buttons/TextButton";
import { useSearchContext } from "client/common/hooks";
import { ProductListSource } from "client/analytics/types";

import { ProductSearchSearch } from "../types";
import { useProductFilterOptionState, useProductSearchQuery } from "../hooks";
import { PRODUCT_SEARCH_FILTER_SCOPE } from "../hooks/constants";
import ProductCardList from "./ProductCardList";
import ProductSearchFilterModal from "./ProductSearchFilterModal";
import { getProductFiltersFromSearch } from "../utils";
import ProductSearchFilterTags from "./ProductSearchFilterTags";

import styles from "../styles/ProductSearchMobile.module.less";

type ProductSearchMobileInputProps = {
  value: string;
  onSearch: (value: string) => void;
};

const ProductSearchMobileInput = ({ value, onSearch }: ProductSearchMobileInputProps) => {
  const { searchInputRef } = useSearchContext();
  const [input, setInput] = useState(value);

  const debounceInput = useDebounce(input);

  // Automatically blur the input when scrolling down similar to native OS
  useEffect(() => {
    const searchEl = searchInputRef.current;

    if (!searchEl) {
      return () => undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.length && !entries[0].isIntersecting) {
          searchInputRef.current?.blur();
        }
      },
      {
        rootMargin: "120px",
      },
    );

    observer.observe(searchEl);

    return () => {
      observer.unobserve(searchEl);
    };
  }, [searchInputRef]);

  useEffect(() => {
    setInput(value);
  }, [value]);

  useEffect(() => {
    if (debounceInput !== value) {
      onSearch(debounceInput);
    }
    /* onSearch is okay to only change when debounceInput does */
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [debounceInput]);

  const handleIconClick = () => {
    onSearch("");
    searchInputRef.current?.focus();
  };

  // Allow submit to blur the input and hide the keyboard
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    searchInputRef.current?.blur();
  };

  return (
    <form onSubmit={handleSubmit} className={styles.search}>
      <input
        ref={searchInputRef}
        value={input}
        placeholder="Search for products..."
        onChange={(e) => setInput(e.target.value)}
        className={styles.input}
      />
      <SearchIcon onClick={handleIconClick} isActive={!!input?.length} className={styles.icon} />
    </form>
  );
};

type ProductSearchMobileProps = {
  search: WithStringValues<ProductSearchSearch>;
  onUpdateSearch: (params: Partial<ProductSearchSearch>) => void;
  onSearch: (value: string) => void;
  onClose: () => void;
};

const ProductSearchMobile = (props: ProductSearchMobileProps) => {
  const { search, onUpdateSearch, onSearch, onClose } = props;
  const searchRef = useRef<HTMLDivElement>(null);

  const [isFiltering, setIsFiltering] = useState(false);

  const { data: products, isLoading, error, isRefetching } = useProductSearchQuery(search);

  const { data: filterOptionState } = useProductFilterOptionState(PRODUCT_SEARCH_FILTER_SCOPE);

  const filters = getProductFiltersFromSearch(search);

  // Reset scroll on page change
  useEffect(() => {
    searchRef.current?.scrollIntoView();
  }, [search.page]);

  const renderContent = () => {
    if (isLoading) {
      return <Results.Loading description="Loading products" />;
    }

    if (error || !products) {
      return <Results.FetchError error={error} entityName="result" />;
    }

    if (!products.results.length) {
      return <Results.None description={`Could not find any results for "${search.q}"`} />;
    }

    return (
      <ProductCardList
        results={products.results}
        listSource={ProductListSource.SearchResults}
        searchTerm={search.q}
        paginator={products.paginator}
        onPageChange={(page) => onUpdateSearch({ page })}
        entityName="result"
        className={styles.main}
      />
    );
  };

  return (
    <ModalPortal>
      <div className={styles.container}>
        <div className={styles.searcher} ref={searchRef}>
          <div className={styles.searchbar}>
            <ProductSearchMobileInput value={search.q} onSearch={onSearch} />
            <TextButton className={styles.cancel} type="button" onClick={onClose}>
              Cancel
            </TextButton>
          </div>
          {products && (
            <div className={styles.top}>
              <div className={styles.info}>
                <div className={styles.filters}>
                  <PillButton variant="secondary" size="xs" onClick={() => setIsFiltering(true)}>
                    <span className={styles.filter}>
                      <Icon icon={faFilter} className={styles.icon} /> Filter
                    </span>
                  </PillButton>
                  {filterOptionState && (
                    <ProductSearchFilterModal
                      options={filterOptionState.filters}
                      isVisible={isFiltering}
                      onClose={() => setIsFiltering(false)}
                      value={filters}
                      onChange={onUpdateSearch}
                    />
                  )}
                </div>
                <div className={styles.count}>
                  {isRefetching ? <Spinner /> : toCount(products.paginator.count, "product")}
                </div>
              </div>
              {filterOptionState && (
                <div className={styles.tags}>
                  <ProductSearchFilterTags
                    options={filterOptionState.filters}
                    value={filters}
                    onChange={onUpdateSearch}
                  />
                </div>
              )}
            </div>
          )}
        </div>
        <div>{renderContent()}</div>
      </div>
    </ModalPortal>
  );
};

export default ProductSearchMobile;
